import { useState, useEffect } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useGoogleLogin } from "@react-oauth/google";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, Mail, Loader2, Gift, Lock, Eye, EyeOff, KeyRound, CheckCircle2, X, PlayCircle } from "lucide-react";
import { NeuralBackground } from "../components/NeuralBackground";
import { startDemoTour } from "../components/DemoAutoTour";

const API = process.env.REACT_APP_BACKEND_URL;

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [localOtpEnabled, setLocalOtpEnabled] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSuccess, setForgotSuccess] = useState(false);
  const { requestOTP, loginWithPassword, otpEnabled } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref");

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleSuccess = async (code) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API}/api/auth/google-oauth`, { code });
      const { access_token, user } = res.data;
      localStorage.setItem("tribe_token", access_token);
      toast.success(`Welcome${user?.name ? ", " + user.name : ""}!`);
      window.location.href = "/feed";
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google sign-in failed");
      setLoading(false);
    }
  };

  const googleLogin = useGoogleLogin({
    flow: "auth-code",
    onSuccess: (codeResp) => handleGoogleSuccess(codeResp.code),
    onError: () => toast.error("Google sign-in cancelled"),
  });

  // Fetch login mode directly as well for reliability
  useEffect(() => {
    axios.get(`${API}/api/auth/login-mode`).then(res => {
      setLocalOtpEnabled(res.data.otp_enabled);
    }).catch(() => setLocalOtpEnabled(false));
  }, []);

  const isOtpMode = localOtpEnabled !== null ? localOtpEnabled : otpEnabled;

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!forgotEmail || !forgotEmail.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setForgotLoading(true);
    try {
      await axios.post(`${API}/api/auth/forgot-password`, { email: forgotEmail.trim().toLowerCase() });
      setForgotSuccess(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    }
    setForgotLoading(false);
  };

  const handleOTPSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email");
      return;
    }
    setLoading(true);
    try {
      const result = await requestOTP(email);
      toast.success("OTP sent to your email!");
      navigate("/verify-otp", { 
        state: { email, demoOTP: result.demo_otp, isNewUser: result.is_new_user, referralCode: refCode } 
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send OTP");
    } finally { setLoading(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const trimmedEmail = email.trim().toLowerCase();
    const trimmedPassword = password.trim();
    if (!trimmedEmail || !trimmedEmail.includes("@")) { toast.error("Please enter a valid email"); return; }
    if (!trimmedPassword) { toast.error("Please enter your password"); return; }
    setLoading(true);
    try {
      const result = await loginWithPassword(trimmedEmail, trimmedPassword);
      if (result.requires_2fa) {
        navigate("/verify-otp", { state: { email: trimmedEmail, requires2FA: true, tempToken: result.temp_token } });
      } else if (result.needs_username) {
        navigate("/setup-username");
      } else {
        navigate("/feed");
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      let msg;
      if (!error.response) {
        msg = "Unable to reach server. Please try again in a moment.";
      } else if (error.response.status === 429) {
        msg = "Too many attempts. Please wait a minute and try again.";
      } else if (typeof detail === "string") {
        msg = detail;
      } else if (Array.isArray(detail)) {
        msg = detail.map(e => e.msg || "").join(" ") || "Login failed";
      } else {
        msg = "Login failed. Please check your email and password.";
      }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0F] flex flex-col px-6 py-8 relative">
      <NeuralBackground />
      <Link to="/welcome" className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8 relative z-10" data-testid="back-btn">
        <ArrowLeft className="w-5 h-5" /> Back
      </Link>

      <div className="flex-1 flex flex-col items-center justify-center max-w-md mx-auto w-full relative z-10">
        <div className="w-36 h-36 md:w-44 md:h-44 rounded-2xl flex items-center justify-center p-4 mb-6"><img src="/brain-logo.png?v=4" alt="AIxTribe" className="w-full h-full object-contain rounded-xl" style={{ mixBlendMode: "screen" }} /></div>
        <h1 className="text-2xl font-bold text-white mb-2 font-heading">Welcome to AIxTribe</h1>
        <p className="text-gray-400 text-center mb-8">
          {showEmailForm ? (isOtpMode ? "Enter your email to continue" : "Login with your email and password") : "Choose how you'd like to sign in"}
        </p>

        {refCode && (
          <div className="w-full bg-tribe-purple/10 border border-tribe-purple/20 rounded-xl p-3 mb-4 flex items-center gap-2" data-testid="referral-banner">
            <Gift className="w-4 h-4 text-tribe-purple flex-shrink-0" />
            <p className="text-tribe-purple text-sm">You were referred by a friend! Sign up to activate the referral.</p>
          </div>
        )}

        {!showEmailForm ? (
          <div className="w-full space-y-4">
            {/* Continue with Google — uses @react-oauth/google popup, code flow. REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH */}
            <button onClick={() => googleLogin()} className="w-full h-14 bg-[#1A1A24] hover:bg-[#222230] border border-[#2A2A3A] rounded-xl flex items-center justify-center gap-3 text-white font-semibold transition-colors" data-testid="google-login-btn">
              <svg width="20" height="20" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
              Continue with Google
            </button>

            {/* Divider */}
            <div className="flex items-center gap-3 my-2">
              <div className="flex-1 h-px bg-[#2A2A3A]" />
              <span className="text-gray-600 text-xs">or</span>
              <div className="flex-1 h-px bg-[#2A2A3A]" />
            </div>

            {/* Continue with Email */}
            <button onClick={() => setShowEmailForm(true)} className="w-full h-14 bg-[#15151F] hover:bg-[#1A1A24] border border-[#2A2A3A] rounded-xl flex items-center justify-center gap-3 text-gray-300 font-semibold transition-colors" data-testid="email-login-btn">
              <Mail className="w-5 h-5" />
              Continue with Email
            </button>

            <p className="text-gray-600 text-xs text-center mt-4">
              By continuing, you agree to our <Link to="/terms" className="text-gray-400 underline">Terms of Service</Link> and <Link to="/privacy" className="text-gray-400 underline">Privacy Policy</Link>.
            </p>

            <button onClick={() => startDemoTour()} className="w-full mt-2 inline-flex items-center justify-center gap-2 text-tribe-cyan text-sm hover:text-white transition-colors" data-testid="login-watch-demo">
              <PlayCircle className="w-4 h-4" /> See how AIxTribe works (2 min demo)
            </button>

            <p className="text-gray-500 text-sm mt-4 text-center">
              Don't have an account? <Link to="/register" className="text-tribe-cyan hover:underline" data-testid="register-link">Register here</Link>
            </p>
          </div>
        ) : (
          <div className="w-full">
            <button onClick={() => setShowEmailForm(false)} className="text-gray-500 text-sm hover:text-white mb-4 flex items-center gap-1 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to login options
            </button>

        <form onSubmit={isOtpMode ? handleOTPSubmit : handlePasswordSubmit} className="w-full space-y-4">
          <div className="relative">
            <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
            <Input
              type="email" placeholder="your@email.com" value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="h-14 pl-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl"
              data-testid="email-input"
            />
          </div>

          {!isOtpMode && (
            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
              <Input
                type={showPassword ? "text" : "password"} placeholder="Password" value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="h-14 pl-12 pr-12 bg-[#15151F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl"
                data-testid="password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          )}

          {!isOtpMode && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setShowForgotPassword(true); setForgotEmail(email); setForgotSuccess(false); }}
                className="text-tribe-cyan text-sm hover:underline"
                data-testid="forgot-password-link"
              >
                Forgot Password?
              </button>
            </div>
          )}

          <Button type="submit" className="w-full h-14 text-lg font-bold gradient-cyan-purple text-white rounded-xl btn-glow" disabled={loading} data-testid="continue-btn">
            {loading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Processing...</> : isOtpMode ? "Continue with Email" : "Login"}
          </Button>
        </form>

        <p className="text-gray-500 text-sm mt-6 text-center">
          {isOtpMode ? "We'll send you a one-time password to verify your email" : (
            <>Don't have an account? <Link to="/register" className="text-tribe-cyan hover:underline" data-testid="register-link-email">Register here</Link></>
          )}
        </p>
          </div>
        )}
      </div>

      {/* Forgot Password Modal */}
      {showForgotPassword && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowForgotPassword(false)}>
          <div className="bg-[#15151F] border border-[#2A2A3A] rounded-2xl w-full max-w-md p-6 relative" onClick={e => e.stopPropagation()} data-testid="forgot-password-modal">
            <button onClick={() => setShowForgotPassword(false)} className="absolute top-4 right-4 text-gray-500 hover:text-white">
              <X className="w-5 h-5" />
            </button>

            {!forgotSuccess ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-lg bg-tribe-cyan/10 flex items-center justify-center">
                    <KeyRound className="w-5 h-5 text-tribe-cyan" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">Forgot Password?</h2>
                    <p className="text-gray-500 text-xs">Enter your registered email to request a reset</p>
                  </div>
                </div>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                    <Input
                      type="email"
                      placeholder="Enter your registered email"
                      value={forgotEmail}
                      onChange={e => setForgotEmail(e.target.value)}
                      className="h-12 pl-12 bg-[#0A0A0F] border-[#2A2A3A] text-white placeholder:text-gray-500 rounded-xl"
                      data-testid="forgot-email-input"
                      autoFocus
                    />
                  </div>
                  <Button type="submit" disabled={forgotLoading} className="w-full h-12 gradient-cyan-purple text-white font-semibold rounded-xl" data-testid="forgot-submit-btn">
                    {forgotLoading ? <Loader2 className="w-5 h-5 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />}
                    Submit Request
                  </Button>
                </form>
                <p className="text-gray-600 text-xs mt-3 text-center">Our admin will reset your password and you'll receive it via email.</p>
              </>
            ) : (
              <div className="text-center py-4" data-testid="forgot-success">
                <div className="w-16 h-16 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
                <h2 className="text-lg font-bold text-white mb-2">New Password Sent!</h2>
                <p className="text-gray-400 text-sm mb-4">
                  A new auto-generated password has been sent to your email. Check your inbox and log in with the new password.
                </p>
                <p className="text-gray-500 text-xs mb-4">After logging in, you can change your password from your profile.</p>
                <Button onClick={() => setShowForgotPassword(false)} variant="outline" className="border-[#2A2A3A] text-gray-300">
                  Back to Login
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
